import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { API_BASE_URL } from "../config/apiConfig";
import { getRealImageUrl } from "../utils/getProductImage";
import { cssVars } from "../theme";
import BulbCutout from "../assets/products/topseller-cutouts/3.png";
import DownlightCutout from "../assets/products/topseller-cutouts/9.png";
import PanelCutout from "../assets/products/topseller-cutouts/28.png";
import FloodCutout from "../assets/products/topseller-cutouts/31.png";
import RopeCutout from "../assets/products/topseller-cutouts/35.png";

/* Presentation-only transparent cutouts (already generated for the Top
   Selling section) reused here so the bulb/product visualization reads
   as an isolated premium product shot rather than a boxed photo. Mirrors
   the same keyword logic as utils/getProductImage.js; that shared util
   itself is untouched. */
const VIZ_CUTOUT_MAP = [
  { keywords: ["bulb", "candle"], image: BulbCutout },
  { keywords: ["downlight", "down light", "cob", "smd", "ice light"], image: DownlightCutout },
  { keywords: ["batten", "tube"], image: DownlightCutout },
  { keywords: ["panel"], image: PanelCutout },
  { keywords: ["flood", "street"], image: FloodCutout },
  { keywords: ["rope", "strip", "decorative"], image: RopeCutout },
];

function getVizCutout(item) {
  if (!item) return BulbCutout;
  // A real uploaded image always wins over the local cutout fallback.
  const realUrl = getRealImageUrl(item);
  if (realUrl) return realUrl;
  const haystack = [item.category, item.subcategory, item.name, item.title, item.sub, item.product]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const match = VIZ_CUTOUT_MAP.find(({ keywords }) => keywords.some((kw) => haystack.includes(kw)));
  return match ? match.image : BulbCutout;
}

// Helper: extract wattage number from a label like "12W Cool White"
function parseWatt(label) {
  const match = /([\d.]+)\s*W/i.exec(String(label));
  return match ? parseFloat(match[1]) : null;
}

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Animates a number smoothly from its previous value to `value` using
   framer-motion, instead of snapping — same underlying value, just an
   eased visual transition. */
function useAnimatedNumber(value) {
  const mv = useMotionValue(value);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    if (REDUCED_MOTION) {
      mv.set(value);
      setDisplay(value);
      return;
    }
    const controls = animate(mv, value, {
      duration: 0.7,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return display;
}

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h7l-1 8 10-12h-7z" />
  </svg>
);
const LeafIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 3C7 5 5 8 5 12a7 7 0 0 0 14 0c0-1.5-.5-3-1.5-4.5C16 9 14 9 13 7c-.8-1.5-1-2.7-2-4z" />
  </svg>
);
const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l8 3v6c0 5-3.4 9-8 11-4.6-2-8-6-8-11V5z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const BulbIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.44 1 1.2 1 2.1h5c0-.9.4-1.66 1-2.1A6 6 0 0 0 12 3z" />
  </svg>
);
const MinusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14" /></svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
);
const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5l-7 7 7 7" /></svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
);

const BENEFIT_STRIP = [
  { icon: <BoltIcon />, text: "Up to 85% Energy Saving" },
  { icon: <SunIcon />, text: "High Brightness" },
  { icon: <ClockIcon />, text: "Long Life Span" },
  { icon: <ShieldIcon />, text: "1-Year Warranty" },
  { icon: <LeafIcon />, text: "Eco Friendly" },
];

export default function HLCSavingsCalculator() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedSub, setSelectedSub] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedModel, setSelectedModel] = useState("");

  const [currentW, setCurrentW] = useState(60);
  const [hours, setHours] = useState(8);
  const [bulbs, setBulbs] = useState(5);
  const [rate, setRate] = useState(65);

  const [scrollState, setScrollState] = useState({ atStart: true, atEnd: true });
  const cardTrackRef = useRef(null);
  const dragRef = useRef({ down: false, moved: false, startX: 0, startScroll: 0 });

  /* ── Fetch products: adminhlc-api (MySQL) — unchanged from prior implementation ── */
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/products/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch products:", err);
        if (!cancelled) {
          setProducts([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Build cascading tree: category → productName → [wattModels] — unchanged ── */
  const TREE = useMemo(() => {
    const tree = {};
    products.forEach((item) => {
      const main = item.category || "Uncategorized";
      const prod = item.name || "";
      const watts = Array.isArray(item.wattColourModel) ? item.wattColourModel : [];
      if (!tree[main]) tree[main] = {};
      if (!tree[main][prod]) tree[main][prod] = { models: new Set(), items: [] };
      watts.forEach((w) => tree[main][prod].models.add(w));
      tree[main][prod].items.push(item);
    });

    const result = {};
    Object.keys(tree).sort().forEach((main) => {
      result[main] = {};
      Object.keys(tree[main]).sort().forEach((prod) => {
        result[main][prod] = {
          models: [...tree[main][prod].models].sort(),
          items: tree[main][prod].items,
        };
      });
    });
    return result;
  }, [products]);

  const subList = useMemo(() => Object.keys(TREE), [TREE]);

  /* ── Flatten the tree into one card per watt/model, for the premium
     product-selection row (replaces the old 3-level dropdown cascade) ── */
  const flatCards = useMemo(() => {
    const list = [];
    subList.forEach((sub) => {
      Object.keys(TREE[sub]).forEach((prod) => {
        const entry = TREE[sub][prod];
        entry.models.forEach((model) => {
          const watt = parseWatt(model);
          if (watt === null) return;
          const item =
            entry.items.find(
              (it) => Array.isArray(it.wattColourModel) && it.wattColourModel.includes(model)
            ) || entry.items[0];
          list.push({
            key: `${sub}__${prod}__${model}`,
            sub,
            prod,
            model,
            watt,
            image: getVizCutout(item),
          });
        });
      });
    });
    return list;
  }, [subList, TREE]);

  /* ── Auto-select the first available product once data loads, so the
     experience is immediately interactive rather than empty ── */
  useEffect(() => {
    if (!loading && flatCards.length && !selectedModel) {
      const first = flatCards[0];
      setSelectedSub(first.sub);
      setSelectedProduct(first.prod);
      setSelectedModel(first.model);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, flatCards]);

  /* ── Derive product image: pick from matched product items, using the
     transparent cutout for the premium visualization panel ── */
  const promoImage = useMemo(() => {
    if (selectedSub && selectedProduct) {
      const items = TREE[selectedSub]?.[selectedProduct]?.items || [];
      for (const item of items) {
        const img = getVizCutout(item);
        if (img) return img;
      }
    }
    return BulbCutout;
  }, [selectedSub, selectedProduct, TREE]);

  const selectedWatt = selectedModel ? parseWatt(selectedModel) : null;

  function selectCard(card) {
    setSelectedSub(card.sub);
    setSelectedProduct(card.prod);
    setSelectedModel(card.model);
  }

  /* ── Carousel: scroll-position tracking for disabled arrow states + edge fades ── */
  function updateScrollState() {
    const track = cardTrackRef.current;
    if (!track) return;
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    setScrollState({ atStart, atEnd });
  }

  useEffect(() => {
    updateScrollState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatCards]);

  useEffect(() => {
    const track = cardTrackRef.current;
    if (!track) return;
    function onWheel(e) {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        track.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    }
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  }, [flatCards.length]);

  function scrollCards(dir) {
    const track = cardTrackRef.current;
    if (!track) return;
    track.scrollBy({ left: dir * (track.clientWidth * 0.7), behavior: "smooth" });
  }

  function handlePointerDown(e) {
    const track = cardTrackRef.current;
    if (!track) return;
    dragRef.current = { down: true, moved: false, startX: e.clientX, startScroll: track.scrollLeft };
  }
  function handlePointerMove(e) {
    const d = dragRef.current;
    if (!d.down) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 4) d.moved = true;
    if (d.moved) cardTrackRef.current.scrollLeft = d.startScroll - dx;
  }
  function handlePointerUp() {
    dragRef.current.down = false;
  }
  function handleCardClick(card) {
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    selectCard(card);
  }

  /* ── Live savings calculation — SAME formula as before, just recomputed
     reactively on every input change instead of on a "Calculate" click ──
     cur/led/save/month/year/energy are unchanged:
       cur  = (currentWatt × bulbs × hours × 30) / 1000
       led  = (ledWatt     × bulbs × hours × 30) / 1000
       save = max(cur - led, 0)
       month = round(save × rate); year = round(save × rate × 12) */
  const results = useMemo(() => {
    const W = Number(currentW);
    const hrs = Number(hours);
    const b = Number(bulbs);
    const r = Number(rate);
    const LED = selectedWatt;

    if (!W || !hrs || !b || !r || LED === null) {
      return { month: 0, year: 0, energy: 0, ready: false };
    }

    const cur = (W * b * hrs * 30) / 1000;
    const led = (LED * b * hrs * 30) / 1000;
    const save = Math.max(cur - led, 0);

    return {
      month: Math.round(save * r),
      year: Math.round(save * r * 12),
      energy: Math.round(save * 10) / 10,
      ready: true,
    };
  }, [currentW, hours, bulbs, rate, selectedWatt]);

  const animatedMonth = useAnimatedNumber(results.month);
  const animatedYear = useAnimatedNumber(results.year);
  const animatedEnergy = useAnimatedNumber(results.energy);

  /* ── Subtle mouse-parallax on the product visualization ── */
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const springX = useTransform(rotateX, [-1, 1], [8, -8]);
  const springY = useTransform(rotateY, [-1, 1], [-8, 8]);

  function handleVizMouseMove(e) {
    if (REDUCED_MOTION) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rotateX.set(py * 2);
    rotateY.set(px * 2);
  }
  function handleVizMouseLeave() {
    rotateX.set(0);
    rotateY.set(0);
  }

  const wattPct = ((currentW - 20) / (150 - 20)) * 100;
  const hoursPct = ((hours - 1) / (24 - 1)) * 100;
  const ratePct = ((rate - 10) / (100 - 10)) * 100;

  return (
    <>
      <style>{`
        .full-bg-section {
          position: relative;
          left: 50%;
          right: 50%;
          margin-left: -50vw;
          margin-right: -50vw;
          width: 100vw;
          overflow: hidden;
          padding: 0;
        }

        #hlc-savings-section {
          background: var(--hlc-gradient-bg);
          color: var(--hlc-text);
          font-family: "Open Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
          padding: clamp(28px, 3.2vw, 44px) 0;
        }

        .ec-wrap { width: min(1280px, 94vw); margin: 0 auto; box-sizing: border-box; }
        .ec-wrap * { box-sizing: border-box; }

        /* ---------- header ---------- */
        .ec-head { text-align: center; margin-bottom: clamp(14px, 2vw, 22px); }
        .ec-head__badge {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 6px 13px; margin-bottom: 10px;
          background: rgba(107, 209, 138, 0.08); border: 1px solid rgba(107, 209, 138, 0.35);
          border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
          color: var(--hlc-success);
        }
        .ec-head__badge svg { width: 13px; height: 13px; }
        .ec-head h2 {
          margin: 0 0 6px; font-size: clamp(26px, 4vw, 40px); font-weight: 800;
          line-height: 1.1; letter-spacing: -0.01em;
        }
        .ec-head h2 span {
          background: var(--hlc-gradient-savings);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .ec-head p { margin: 0 auto; max-width: 500px; font-size: 14px; line-height: 1.5; color: var(--hlc-text-secondary); }

        /* ---------- 3-col experience ---------- */
        .ec-experience {
          display: grid;
          grid-template-columns: minmax(260px, 320px) minmax(300px, 1fr) minmax(260px, 340px);
          gap: 16px;
          align-items: stretch;
        }

        .ec-glass {
          background: var(--hlc-surface);
          border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius);
          backdrop-filter: blur(var(--hlc-blur));
          -webkit-backdrop-filter: blur(var(--hlc-blur));
          box-shadow: var(--hlc-shadow-card);
        }

        /* ---------- left panel: current setup ---------- */
        .ec-panel { padding: 16px 18px 18px; display: flex; flex-direction: column; }
        .ec-panel__title {
          display: flex; align-items: center; gap: 9px;
          margin: 0 0 14px; font-size: 12px; font-weight: 800;
          letter-spacing: 0.12em; text-transform: uppercase; color: rgba(255,255,255,0.9);
        }
        .ec-panel__title svg { width: 16px; height: 16px; color: var(--hlc-magenta-light); }

        .ec-control { margin-bottom: 14px; }
        .ec-control:last-child { margin-bottom: 0; }
        .ec-control__head {
          display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 6px;
        }
        .ec-control__label {
          font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
          color: var(--hlc-text-secondary);
        }
        .ec-control__value { font-size: 16px; font-weight: 800; color: var(--hlc-gold); }

        .ec-slider {
          -webkit-appearance: none; appearance: none; width: 100%; height: 5px;
          border-radius: 999px; outline: none; cursor: pointer;
          background: rgba(255,255,255,0.12);
        }
        .ec-slider::-webkit-slider-thumb {
          -webkit-appearance: none; width: 17px; height: 17px; border-radius: 50%;
          background: linear-gradient(135deg, var(--hlc-gold), var(--hlc-gold-deep));
          box-shadow: 0 0 0 4px rgba(255,213,90,0.18), 0 4px 10px rgba(0,0,0,0.4);
          cursor: pointer; transition: transform 0.2s ease;
        }
        .ec-slider::-webkit-slider-thumb:hover { transform: scale(1.12); }
        .ec-slider::-moz-range-thumb {
          width: 17px; height: 17px; border-radius: 50%; border: none;
          background: linear-gradient(135deg, var(--hlc-gold), var(--hlc-gold-deep));
          box-shadow: 0 0 0 4px rgba(255,213,90,0.18), 0 4px 10px rgba(0,0,0,0.4);
          cursor: pointer;
        }
        .ec-slider:focus-visible { outline: 2px solid var(--hlc-gold); outline-offset: 3px; }
        .ec-slider__scale {
          display: flex; justify-content: space-between; margin-top: 5px;
          font-size: 10px; color: rgba(255,255,255,0.4);
        }

        .ec-stepper {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--hlc-border);
          border-radius: 10px; padding: 5px;
        }
        .ec-stepper__btn {
          width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--hlc-border);
          background: rgba(255,255,255,0.06); color: #fff; display: inline-flex;
          align-items: center; justify-content: center; cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
        }
        .ec-stepper__btn svg { width: 15px; height: 15px; }
        .ec-stepper__btn:hover { background: var(--hlc-magenta); border-color: var(--hlc-magenta); transform: translateY(-1px); }
        .ec-stepper__value { font-size: 17px; font-weight: 800; color: #fff; min-width: 36px; text-align: center; }

        .ec-loading {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 22px 0; font-size: 12px; color: rgba(255,255,255,0.6);
        }
        .ec-spinner {
          width: 15px; height: 15px; border: 2px solid rgba(255,255,255,0.2);
          border-top-color: var(--hlc-gold); border-radius: 50%; animation: ec-spin 0.7s linear infinite;
        }
        @keyframes ec-spin { to { transform: rotate(360deg); } }

        /* ---------- center: savings visualization ---------- */
        .ec-center { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 12px 16px; gap: 14px; }
        .ec-center__title {
          font-size: 12px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase;
          color: rgba(255,255,255,0.6); margin: 0;
        }

        .ec-orb {
          position: relative; width: min(196px, 56vw); aspect-ratio: 1/1;
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          background: var(--hlc-gradient-glow);
          box-shadow:
            0 0 46px rgba(155,31,108,0.38),
            0 0 70px rgba(255,213,90,0.10),
            inset 0 0 36px rgba(0,0,0,0.5);
        }
        .ec-orb::before {
          content: ""; position: absolute; inset: -12px; border-radius: 50%;
          background: conic-gradient(from 0deg, transparent 0deg, rgba(255,213,90,0.5) 60deg, transparent 140deg, rgba(155,31,108,0.6) 240deg, transparent 320deg);
          filter: blur(6px); animation: ec-orbit 10s linear infinite;
        }
        .ec-orb::after {
          content: ""; position: absolute; inset: 9px; border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.14);
        }
        @keyframes ec-orbit { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .ec-orb::before { animation: none; } }

        .ec-orb__content { position: relative; z-index: 2; text-align: center; padding: 0 12px; }
        .ec-orb__label {
          display: block; font-size: 9.5px; font-weight: 800; letter-spacing: 0.16em;
          text-transform: uppercase; color: rgba(255,255,255,0.62); margin-bottom: 5px;
        }
        .ec-orb__value {
          display: block; font-size: clamp(21px, 3vw, 28px); font-weight: 800;
          letter-spacing: -0.02em; color: var(--hlc-gold); line-height: 1.1;
        }
        .ec-orb__sub {
          display: block; margin-top: 5px; font-size: 9.5px; font-weight: 700;
          letter-spacing: 0.14em; text-transform: uppercase; color: rgba(255,255,255,0.5);
        }

        .ec-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; }
        .ec-metric {
          background: rgba(255,255,255,0.045); border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius-sm); padding: 10px 10px; text-align: center;
        }
        .ec-metric__label {
          display: block; font-size: 9px; font-weight: 800; letter-spacing: 0.1em;
          text-transform: uppercase; color: rgba(255,255,255,0.55); margin-bottom: 5px;
        }
        .ec-metric__value { font-size: clamp(14px, 1.5vw, 18px); font-weight: 800; color: #fff; }

        /* ---------- right: product visualization ---------- */
        .ec-viz-panel { padding: 14px 14px; display: flex; flex-direction: column; }
        .ec-viz {
          position: relative; flex: 1; min-height: 210px; perspective: 900px;
          display: flex; align-items: center; justify-content: center;
        }
        .ec-viz__ringA, .ec-viz__ringB {
          position: absolute; border-radius: 50%; border: 1px solid rgba(155,31,108,0.3);
        }
        .ec-viz__ringA { width: 78%; height: 78%; }
        .ec-viz__ringB { width: 58%; height: 58%; border-color: rgba(255,213,90,0.18); }
        .ec-viz__platform {
          position: absolute; bottom: 14%; width: 58%; height: 14px; border-radius: 50%;
          background: radial-gradient(ellipse at center, rgba(155,31,108,0.5), transparent 72%);
          filter: blur(4px);
        }
        .ec-viz__bulb {
          position: relative; z-index: 2; width: 56%; max-width: 172px;
          filter: drop-shadow(0 16px 26px rgba(155,31,108,0.4));
        }
        .ec-viz__badge {
          position: absolute; display: flex; align-items: center; gap: 7px;
          padding: 7px 11px; border-radius: var(--hlc-radius-sm);
          background: rgba(10,8,20,0.55); border: 1px solid var(--hlc-border);
          backdrop-filter: blur(10px); font-size: 10px; line-height: 1.2; color: #fff;
          box-shadow: 0 8px 20px rgba(0,0,0,0.35);
        }
        .ec-viz__badge svg { width: 15px; height: 15px; color: var(--hlc-magenta-light); flex-shrink: 0; }
        .ec-viz__badge b { display: block; font-size: 11.5px; }
        .ec-viz__badge span { display: block; color: rgba(255,255,255,0.6); font-size: 9px; }
        .ec-viz__badge--a { top: 3%; left: -4%; }
        .ec-viz__badge--b { top: 42%; right: -8%; }
        .ec-viz__badge--c { bottom: 1%; left: 2%; }

        /* ---------- product selection row ---------- */
        .ec-choose { margin-top: clamp(16px, 2vw, 26px); }
        .ec-choose__head {
          display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
        }
        .ec-choose__title {
          font-size: 12px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase;
          color: rgba(255,255,255,0.7); margin: 0;
        }
        .ec-choose__nav { display: flex; gap: 6px; }
        .ec-choose__navbtn {
          width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--hlc-border);
          background: rgba(255,255,255,0.05); color: #fff; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center;
          transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
        }
        .ec-choose__navbtn svg { width: 14px; height: 14px; }
        .ec-choose__navbtn:hover:not(:disabled) { background: var(--hlc-magenta); border-color: var(--hlc-magenta); transform: translateY(-1px); }
        .ec-choose__navbtn:disabled { opacity: 0.28; cursor: not-allowed; }

        .ec-cardtrack-wrap { position: relative; }
        .ec-cardtrack-wrap::before, .ec-cardtrack-wrap::after {
          content: ""; position: absolute; top: 0; bottom: 6px; width: 28px; z-index: 2;
          pointer-events: none; transition: opacity 0.25s ease;
        }
        .ec-cardtrack-wrap::before { left: 0; background: linear-gradient(90deg, var(--hlc-bg), transparent); opacity: var(--fade-start, 0); }
        .ec-cardtrack-wrap::after { right: 0; background: linear-gradient(270deg, var(--hlc-bg), transparent); opacity: var(--fade-end, 0); }

        .ec-cardtrack {
          display: flex; gap: 10px; overflow-x: auto; scroll-snap-type: x mandatory;
          padding-bottom: 2px; cursor: grab; touch-action: pan-y;
          scrollbar-width: none; -ms-overflow-style: none;
        }
        .ec-cardtrack::-webkit-scrollbar { display: none; height: 0; }
        .ec-cardtrack:active { cursor: grabbing; }
        .ec-prodcard {
          scroll-snap-align: start; flex: 0 0 auto; width: 134px;
          background: rgba(255,255,255,0.04); border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius-sm); padding: 11px 10px; cursor: pointer; text-align: left;
          color: #fff; font-family: inherit; user-select: none;
          transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
        }
        .ec-prodcard:hover { transform: translateY(-3px); border-color: rgba(155,31,108,0.5); }
        .ec-prodcard--active {
          border-color: var(--hlc-magenta-light); background: rgba(155,31,108,0.1);
          box-shadow: 0 10px 26px rgba(155,31,108,0.24);
        }
        .ec-prodcard__img {
          width: 100%; aspect-ratio: 5/4; display: flex; align-items: center; justify-content: center;
          margin-bottom: 7px; pointer-events: none;
        }
        .ec-prodcard__img img { width: 100%; height: 100%; object-fit: contain; }
        .ec-prodcard__name {
          font-size: 9.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
          color: rgba(255,255,255,0.6); margin-bottom: 2px;
        }
        .ec-prodcard__watt { font-size: 15px; font-weight: 800; color: #fff; }
        .ec-prodcard--active .ec-prodcard__watt { color: var(--hlc-magenta-light); }

        /* ---------- bottom benefit strip ---------- */
        .ec-strip {
          margin-top: clamp(14px, 1.8vw, 22px); padding-top: 14px;
          border-top: 1px solid var(--hlc-border);
          display: flex; flex-wrap: wrap; justify-content: center; gap: 18px;
        }
        .ec-strip__item {
          display: flex; align-items: center; gap: 7px; font-size: 12px;
          color: rgba(255,255,255,0.68); font-weight: 600;
        }
        .ec-strip__item svg { width: 15px; height: 15px; color: var(--hlc-magenta-light); flex-shrink: 0; }

        /* ---------- responsive ---------- */
        @media (max-width: 1100px) {
          .ec-experience { grid-template-columns: 1fr 1fr; }
          .ec-center { grid-column: 1 / -1; order: -1; }
        }
        @media (max-width: 700px) {
          .ec-experience { grid-template-columns: 1fr; }
          .ec-center { order: -1; }
          .ec-viz-panel { order: 0; }
          .ec-panel { order: 1; }
          .ec-viz { min-height: 0; flex-direction: column; gap: 8px; }
          .ec-viz__badge { position: static; margin-top: 0; width: 100%; }
          .ec-viz__badge--a, .ec-viz__badge--b, .ec-viz__badge--c { top: auto; left: auto; right: auto; bottom: auto; }
          .ec-cardtrack { gap: 8px; }
          .ec-prodcard { width: 118px; padding: 10px 9px; }
        }
      `}</style>

      <section
        id="hlc-savings-section"
        className="full-bg-section"
        aria-label="HLC LED Energy Savings Calculator"
        style={cssVars}
      >
        <div className="ec-wrap">
          <header className="ec-head">
            <span className="ec-head__badge"><LeafIcon /> Energy Efficient Choice</span>
            <h2>See What You Could <span>Save</span></h2>
            <p>Switch to HLC LEDs and experience the difference.</p>
          </header>

          <div className="ec-experience">
            {/* LEFT — current setup */}
            <aside className="ec-glass ec-panel">
              <h3 className="ec-panel__title"><BulbIcon /> Your Current Setup</h3>

              <div className="ec-control">
                <div className="ec-control__head">
                  <span className="ec-control__label">Current Bulb Wattage</span>
                  <span className="ec-control__value">{currentW}W</span>
                </div>
                <input
                  className="ec-slider"
                  type="range"
                  min={20}
                  max={150}
                  step={1}
                  value={currentW}
                  onChange={(e) => setCurrentW(Number(e.target.value))}
                  aria-label="Current bulb wattage in watts"
                  style={{
                    background: `linear-gradient(to right, var(--hlc-gold) ${wattPct}%, rgba(255,255,255,0.12) ${wattPct}%)`,
                  }}
                />
                <div className="ec-slider__scale"><span>20W</span><span>150W</span></div>
              </div>

              <div className="ec-control">
                <div className="ec-control__head">
                  <span className="ec-control__label">Daily Usage</span>
                  <span className="ec-control__value">{hours} {hours === 1 ? "Hour" : "Hours"}/Day</span>
                </div>
                <input
                  className="ec-slider"
                  type="range"
                  min={1}
                  max={24}
                  step={1}
                  value={hours}
                  onChange={(e) => setHours(Number(e.target.value))}
                  aria-label="Daily usage in hours"
                  style={{
                    background: `linear-gradient(to right, var(--hlc-gold) ${hoursPct}%, rgba(255,255,255,0.12) ${hoursPct}%)`,
                  }}
                />
                <div className="ec-slider__scale"><span>1h</span><span>24h</span></div>
              </div>

              <div className="ec-control">
                <div className="ec-control__head">
                  <span className="ec-control__label">Number of Bulbs</span>
                </div>
                <div className="ec-stepper">
                  <button
                    type="button"
                    className="ec-stepper__btn"
                    aria-label="Decrease number of bulbs"
                    onClick={() => setBulbs((n) => Math.max(1, n - 1))}
                  >
                    <MinusIcon />
                  </button>
                  <span className="ec-stepper__value">{bulbs}</span>
                  <button
                    type="button"
                    className="ec-stepper__btn"
                    aria-label="Increase number of bulbs"
                    onClick={() => setBulbs((n) => Math.min(50, n + 1))}
                  >
                    <PlusIcon />
                  </button>
                </div>
              </div>

              <div className="ec-control">
                <div className="ec-control__head">
                  <span className="ec-control__label">Electricity Cost</span>
                  <span className="ec-control__value">{rate} PKR/kWh</span>
                </div>
                <input
                  className="ec-slider"
                  type="range"
                  min={10}
                  max={100}
                  step={1}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  aria-label="Electricity cost in PKR per kilowatt hour"
                  style={{
                    background: `linear-gradient(to right, var(--hlc-gold) ${ratePct}%, rgba(255,255,255,0.12) ${ratePct}%)`,
                  }}
                />
                <div className="ec-slider__scale"><span>10</span><span>100</span></div>
              </div>
            </aside>

            {/* CENTER — savings orb */}
            <section className="ec-center" aria-label="Your potential savings">
              <p className="ec-center__title">Your Potential Savings</p>

              <div className="ec-orb">
                <div className="ec-orb__content">
                  <span className="ec-orb__label">You Save</span>
                  <strong className="ec-orb__value">
                    {results.ready ? `PKR ${Math.round(animatedMonth).toLocaleString()}` : "Select a product"}
                  </strong>
                  <span className="ec-orb__sub">Every Month</span>
                </div>
              </div>

              <div className="ec-metrics">
                <div className="ec-metric">
                  <span className="ec-metric__label">Yearly Saving</span>
                  <div className="ec-metric__value">
                    {results.ready ? `PKR ${Math.round(animatedYear).toLocaleString()}` : "—"}
                  </div>
                </div>
                <div className="ec-metric">
                  <span className="ec-metric__label">Energy Reduced</span>
                  <div className="ec-metric__value">
                    {results.ready ? `${animatedEnergy.toFixed(1)} kWh` : "—"}
                  </div>
                </div>
              </div>
            </section>

            {/* RIGHT — product visualization */}
            <aside className="ec-glass ec-viz-panel">
              <div className="ec-viz" onMouseMove={handleVizMouseMove} onMouseLeave={handleVizMouseLeave}>
                <div className="ec-viz__ringA" />
                <div className="ec-viz__ringB" />
                <div className="ec-viz__platform" />
                <motion.img
                  src={promoImage}
                  alt={selectedProduct ? `${selectedProduct} ${selectedModel}` : "HLC LED"}
                  className="ec-viz__bulb"
                  style={{ rotateX: springY, rotateY: springX }}
                  animate={REDUCED_MOTION ? {} : { y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="ec-viz__badge ec-viz__badge--a">
                  <BoltIcon />
                  <span><b>{selectedWatt !== null ? `${selectedWatt}W` : "—"}</b><span>Low Power</span></span>
                </div>
                <div className="ec-viz__badge ec-viz__badge--b">
                  <SunIcon />
                  <span><b>High</b><span>Brightness</span></span>
                </div>
                <div className="ec-viz__badge ec-viz__badge--c">
                  <ClockIcon />
                  <span><b>Long</b><span>Life Span</span></span>
                </div>
              </div>
            </aside>
          </div>

          {/* CHOOSE YOUR HLC LED */}
          <div className="ec-choose">
            <div className="ec-choose__head">
              <h3 className="ec-choose__title">Choose Your HLC LED</h3>
              {flatCards.length > 0 && (
                <div className="ec-choose__nav">
                  <button
                    type="button"
                    className="ec-choose__navbtn"
                    aria-label="Scroll left"
                    disabled={scrollState.atStart}
                    onClick={() => scrollCards(-1)}
                  >
                    <ChevronLeft />
                  </button>
                  <button
                    type="button"
                    className="ec-choose__navbtn"
                    aria-label="Scroll right"
                    disabled={scrollState.atEnd}
                    onClick={() => scrollCards(1)}
                  >
                    <ChevronRight />
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="ec-loading"><div className="ec-spinner" /><span>Loading products…</span></div>
            ) : flatCards.length === 0 ? (
              <p className="ec-loading">No products available right now.</p>
            ) : (
              <div
                className="ec-cardtrack-wrap"
                style={{ "--fade-start": scrollState.atStart ? 0 : 1, "--fade-end": scrollState.atEnd ? 0 : 1 }}
              >
                <div
                  className="ec-cardtrack"
                  ref={cardTrackRef}
                  onScroll={updateScrollState}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                >
                  {flatCards.map((card) => {
                    const active = card.key === `${selectedSub}__${selectedProduct}__${selectedModel}`;
                    return (
                      <button
                        type="button"
                        key={card.key}
                        className={`ec-prodcard${active ? " ec-prodcard--active" : ""}`}
                        aria-pressed={active}
                        onClick={() => handleCardClick(card)}
                      >
                        <span className="ec-prodcard__img"><img src={card.image} alt={card.prod} draggable={false} /></span>
                        <span className="ec-prodcard__name">{card.prod}</span>
                        <span className="ec-prodcard__watt">{card.watt}W</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* BOTTOM BENEFIT STRIP */}
          <div className="ec-strip">
            {BENEFIT_STRIP.map((b) => (
              <span className="ec-strip__item" key={b.text}>{b.icon}{b.text}</span>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
